'use client';

import { addToCart } from '@/api/cart';
import { fetchCoatingsShow, fetchLensShow } from '@/api/product';
import { IGoodsDetail, IPrice } from '@/api/types/public';
import { DefaultSelectionProvider } from '@/app/_lens-components/DefaultSelectionContext';
import {
  COATING_COMPONENT,
  LENS_INDEX_COMPONENT,
  LENS_TYPE_COMPONENT,
} from '@/app/_lens-components/constants';
import { useApplyDefaultSelection } from '@/app/_lens-components/hooks/useInitDefaultSelection';
import { useShowLensInfo } from '@/app/_lens-components/hooks/useShowLensInfo';
import ClientLeft from '@/app/_lens-components/layout/ClientLeft';
import Footer from '@/app/_lens-components/layout/Footer';
import Header from '@/app/_lens-components/layout/Header';
import Coating from '@/app/_lens-components/steps/Coating';
import LensType from '@/app/_lens-components/steps/LensType';
import { useLensProcessStore } from '@/app/_lens-components/store/lens-process-store';
import { processClassName } from '@/app/_lens-components/style/responsive';
import {
  BaseCartRequestData,
  CoatingItem,
  ColorItem,
  LensShowResponse,
} from '@/app/_lens-components/types';
import {
  handleCoatingPriceInfo,
  handleColorItemPriceInfo,
  handleGoodsFramePriceInfo,
  handleLensIndexPriceInfo,
  handlerPrescriptionData,
  isCoatingStepInFlow,
  shouldApplyCoatingSelection,
} from '@/app/_lens-components/utils';
import { getLensTypeKey } from '@/app/_lens-components/utils/applyDefaultLensType';
import {
  encodePrevLensTypeSelection,
  mergePrevLensSelection,
  mergePrevLensIndexSelection,
} from '@/app/_lens-components/utils/operatePrevLensSelection';
import LensIndex from '../lens-process/components/steps/LensIndex';
import { IPrescriptionOptions } from '@/components/PrescriptionFormV2/types';
import { getEmptyPrescription } from '@/components/PrescriptionFormV2/utils';
import { useReporting } from '@/hooks/useReporting';
import { useStore } from '@/providers/app-store-provider';
import logger from '@/utils/client-logger';
import { EPrescriptionType } from '@/utils/enums/goods';
import { add } from '@/utils/math';
import { randomString } from '@/utils/tools';
import { useMediaQuery } from '@mui/material';
import { useTranslations } from '@/i18n/use-translations';
import { useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';
import { useWhenCurUserInfoReady } from '@/hooks/useWhenReady';
import { NON_PRESCRIPTION_TYPES } from '@/utils/constants';

export default function ClientPage({
  goodsDetail,
  isRedeem,
  hasDefaultSelection = false,
}: {
  goodsDetail: IGoodsDetail;
  isRedeem: boolean;
  /** 与 lens-process 一致：魔方 lens_process_default_selection */
  hasDefaultSelection?: boolean;
}) {
  const router = useRouter();
  const t = useTranslations('lens-process');
  /** 积分兑换不允许更新「上次选中」 */
  const canModifyPrevSelection = !isRedeem;
  // 配镜流程状态
  const addToCartText = isRedeem ? t('checkout') : t('addToCart');
  // 是否是 frame 配镜步骤
  const isFrameProcess = true;
  const { trackEvent } = useReporting();
  const isWap = useMediaQuery('(max-width:1280px)');
  const setIsGlobalLoading = useStore((state) => state.setIsGlobalLoading);
  const {
    setIsNonPrescriptionType,
    // 商品基本信息
    lensGoodsItem,
    setLensGoodsItem,
    // 是否是积分兑换商品
    setIsRedeem,
    prescriptionType,
    setPrescriptionType,
    setPresTypeOptionName,
    prescription,
    setHasIndexPrice,
    setGoodsFramePriceInfo,
    setTotalPrice,
    setLensTypePriceInfo,
    setLensIndexPriceInfo,
    setCoatingPriceInfo,
    selectedColorItem,
    setSelectedColorItem,
    selectedCoating,
    setSelectedCoating,
    selectedLensType,
    setSelectedLensType,
    selectedBrand,
    setSelectedBrand,
    selectedLensIndexItem,
    setSelectedLensIndexItem,
    SDLensTypePageNextClick,
    SDLensIndexPageNextClick,
    SDAddToCartBtnClick,
    SDAddToCartResult,
    sendPoints,
    setTtqEventId,
    setTrackEventFn,
    SDSendLeaveEvent,
    setCurrency,
    SDEditLensPageView,
    SDEditLensPageClose,
    setCampaignTypeIdList,
    setLensGoodsSensorsData,
    setBtnDisable,
    setStepBtnText,
    setIsLoadingLensShow,
    nowStep,
    setNowStep,
    nowComponent,
    setNowComponent,
    resetStore,
    setBrandExpand,
    initCoating,
  } = useLensProcessStore();

  const {
    initDefaultLensTypeSelection,
    initDefaultLensIndexSelection,
    initDefaultCoatingSelection,
  } = useApplyDefaultSelection();

  const { showLensIndex } = useShowLensInfo(isFrameProcess);

  useEffect(() => {
    if (trackEvent) {
      setTrackEventFn(trackEvent);
    }
  }, [trackEvent, setTrackEventFn]);

  const [allComponent, setAllComponent] = useState<string[]>([
    LENS_TYPE_COMPONENT,
    COATING_COMPONENT,
  ]);
  const [lensIndexTip, setLensIndexTip] = useState('');
  const userInfo = useStore((state) => state.userInfo);
  const visitorId = useStore((state) => state.visitorId);
  const currency = useStore((state) => state.currency);
  const [lensTypeColorData, setLensTypeColorData] = useState<LensShowResponse>([]);
  const [coatingList, setCoatingList] = useState<CoatingItem[]>([]);

  const handleLensTypeSelectedColorItem = (item: ColorItem | null) => {
    setSelectedColorItem(item);
    if (!item?.lens_list?.length) {
      setSelectedLensIndexItem(null);
      return;
    }
    if (hasDefaultSelection) {
      initDefaultLensIndexSelection(item);
      return;
    }
    if (item.lens_list.length === 1) {
      setSelectedLensIndexItem(item.lens_list[0]);
      return;
    }
    const current = selectedLensIndexItem;
    const ok = current != null && item.lens_list.some((x) => `${x.id}` === `${current.id}`);
    if (!ok) setSelectedLensIndexItem(null);
  };

  useWhenCurUserInfoReady(() => {
    setPrescriptionType(EPrescriptionType.FRAME_ONLY);
    setPresTypeOptionName(EPrescriptionType.FRAME_ONLY);
    setIsRedeem(!!isRedeem);
    setCurrency(currency);
    setHasIndexPrice(true);

    let entrance_edit_lens_scene = '';
    let edit_lens_type = '';
    logger.warn('sessionStorage', sessionStorage.getItem('zl-sensors-lens-entrance-scene'));
    if (sessionStorage.getItem('zl-sensors-lens-entrance-scene')) {
      const zlSensorsLensEntranceScene = sessionStorage.getItem('zl-sensors-lens-entrance-scene');
      sessionStorage.removeItem('zl-sensors-lens-entrance-scene');
      const arr = zlSensorsLensEntranceScene.split('|');
      entrance_edit_lens_scene = arr[0] || '';
      edit_lens_type = arr[1] || '';
    }
    if (goodsDetail) {
      const innerLensGoodsDetail = {
        product_id: goodsDetail.product.id,
        product: {
          ...goodsDetail.product,
          exclusion: goodsDetail.product.exclusion
            ? JSON.stringify(goodsDetail.product.exclusion)
            : undefined,
          price_info_pc_v0: goodsDetail.product.price_info_pc_v0,
          price_info_wap_v0: goodsDetail.product.price_info_wap_v0,
          price_info_pc_v1: goodsDetail.product.price_info_pc_v1,
          price_info_wap_v1: goodsDetail.product.price_info_wap_v1,
          price_info_pc_v2: goodsDetail.product.price_info_pc_v2,
          price_info_wap_v2: goodsDetail.product.price_info_wap_v2,
          price_info_pc_v3: goodsDetail.product.price_info_pc_v3,
          price_info_wap_v3: goodsDetail.product.price_info_wap_v3,
          img: goodsDetail.product.image?.product?.[0] || '',
          color: goodsDetail.product.product_frame_color_name,
        },
      };
      setLensGoodsItem(innerLensGoodsDetail);
      const campaignIds = goodsDetail.product?.buried_point_data?.campaign_type_id_list || [];
      setCampaignTypeIdList(campaignIds);
      const framePriceForSensor = handleGoodsFramePriceInfo(
        innerLensGoodsDetail.product,
        !!isRedeem,
        currency,
        userInfo,
      );
      setLensGoodsSensorsData({
        product_view_uuid: randomString() || '',
        shop_sku: goodsDetail.product.shop_sku,
        business_sku: goodsDetail.product.business_sku,
        product_sku: goodsDetail.product.sku,
        product_id: goodsDetail.product.id.toString(),
        product_name: goodsDetail.product.short_name,
        entrance_edit_lens_scene,
        edit_lens_type,
        product_class_name: goodsDetail.product.products_category,
        frame_price: parseFloat(framePriceForSensor.price ?? ''),
        current_frame_price: parseFloat(framePriceForSensor.sell_price ?? ''),
        currency: currency?.name ?? '',
      });
    }
    getLensShow(EPrescriptionType.FRAME_ONLY);
    getCoatingList();

    document.addEventListener('visibilitychange', handleVisibilityChange);
    setTimeout(() => {
      SDEditLensPageView();
    }, 200);
  }, []);

  useEffect(() => {
    return () => {
      try {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
        SDEditLensPageClose();
        logger.warn('SDEditLensPageClose-0');
        resetStore();
      } catch (error) {
        logger.error('Failed to reset resetStore:', error);
      }
    };
  }, []);

  useEffect(() => {
    if (lensGoodsItem) {
      if (userInfo?.id) {
        setTtqEventId(`${userInfo.id}${lensGoodsItem?.product?.shop_sku}`);
      } else {
        setTtqEventId(`${visitorId}${lensGoodsItem?.product?.shop_sku}`);
      }
    }
  }, [userInfo, lensGoodsItem, visitorId, setTtqEventId]);

  useEffect(() => {
    const next: string[] = [LENS_TYPE_COMPONENT];
    const listLen = selectedColorItem?.lens_list?.length ?? 0;
    if (listLen > 1) {
      next.push(LENS_INDEX_COMPONENT);
    }
    if (isCoatingStepInFlow(prescriptionType, selectedColorItem, { isFrameProcess: true })) {
      next.push(COATING_COMPONENT);
    }
    setAllComponent(next);
  }, [selectedColorItem, prescriptionType]);

  useEffect(() => {
    if (
      !shouldApplyCoatingSelection(prescriptionType, selectedColorItem, { isFrameProcess: true })
    ) {
      initCoating();
    }
  }, [prescriptionType, selectedColorItem, initCoating]);

  useEffect(() => {
    if (!hasDefaultSelection || !coatingList.length || selectedCoating || !currency || !userInfo) {
      return;
    }
    if (
      shouldApplyCoatingSelection(prescriptionType, selectedColorItem, { isFrameProcess: true })
    ) {
      initDefaultCoatingSelection({
        coatingList,
        currency,
        userInfo,
        isFrameProcess: true,
      });
    }
  }, [
    hasDefaultSelection,
    coatingList,
    selectedColorItem,
    prescriptionType,
    selectedCoating,
    currency,
    userInfo,
    initDefaultCoatingSelection,
  ]);

  useEffect(() => {
    setNowComponent(allComponent[nowStep] ?? '');
    setIsNonPrescriptionType(
      [EPrescriptionType.NON_PRESCRIPTION, EPrescriptionType.SUNGLASSES_NON_PRESCRIPTION].includes(
        prescriptionType as EPrescriptionType,
      ),
    );
  }, [prescriptionType, nowStep, allComponent, setNowComponent, setIsNonPrescriptionType]);

  useEffect(() => {
    if (nowStep >= allComponent.length && allComponent.length > 0) {
      setNowStep(allComponent.length - 1);
    }
  }, [allComponent, nowStep, setNowStep]);

  useEffect(() => {
    const nextComponent = allComponent[nowStep + 1];
    if (!nextComponent) {
      setStepBtnText(addToCartText);
    } else {
      setStepBtnText(t('next'));
    }
    switch (nowComponent) {
      case LENS_TYPE_COMPONENT:
        setBtnDisable(!selectedColorItem?.lens_color);
        break;
      case LENS_INDEX_COMPONENT:
        setBtnDisable(!selectedLensIndexItem?.index_name);
        break;
      case COATING_COMPONENT:
        setBtnDisable(!selectedCoating);
        break;
      default:
        setBtnDisable(true);
    }
  }, [
    nowStep,
    nowComponent,
    selectedColorItem,
    selectedCoating,
    allComponent,
    addToCartText,
    selectedLensIndexItem,
    setBtnDisable,
    setStepBtnText,
    t,
  ]);

  useEffect(() => {
    if (lensGoodsItem && lensGoodsItem.product) {
      const goodsFramePriceInfo = handleGoodsFramePriceInfo(
        lensGoodsItem.product,
        isRedeem,
        currency,
        userInfo,
      );
      let lensTypePriceInfo: IPrice;
      let lensIndexPriceInfo: IPrice;
      let coatingPriceInfo: IPrice;
      if (selectedColorItem) {
        lensTypePriceInfo = handleColorItemPriceInfo(selectedColorItem, true, currency, userInfo);
      }
      if (selectedLensIndexItem) {
        lensIndexPriceInfo = handleLensIndexPriceInfo(selectedLensIndexItem, currency, userInfo);
      }
      if (
        shouldApplyCoatingSelection(prescriptionType, selectedColorItem, {
          isFrameProcess: true,
        }) &&
        selectedCoating
      ) {
        coatingPriceInfo = handleCoatingPriceInfo(selectedCoating, currency, userInfo);
      }
      setGoodsFramePriceInfo(goodsFramePriceInfo);
      setLensTypePriceInfo(lensTypePriceInfo);
      setLensIndexPriceInfo(lensIndexPriceInfo);
      setCoatingPriceInfo(coatingPriceInfo);
      setTotalPrice({
        currency: currency.name,
        sell_price: add(
          goodsFramePriceInfo.sell_price,
          lensTypePriceInfo?.sell_price || 0,
          showLensIndex ? lensIndexPriceInfo?.sell_price || 0 : 0,
          coatingPriceInfo?.sell_price || 0,
        ).toFixed(2),
        price: add(
          goodsFramePriceInfo.price,
          lensTypePriceInfo?.price || 0,
          showLensIndex ? lensIndexPriceInfo?.price || 0 : 0,
          coatingPriceInfo?.price || 0,
        ).toFixed(2),
      });
    }
  }, [
    lensGoodsItem,
    selectedColorItem,
    selectedCoating,
    selectedLensIndexItem,
    showLensIndex,
    isRedeem,
    currency,
    userInfo,
    setGoodsFramePriceInfo,
    setLensTypePriceInfo,
    setLensIndexPriceInfo,
    setCoatingPriceInfo,
    setTotalPrice,
  ]);

  const handleLeftBack = () => {
    if (nowStep > 0) {
      setNowStep(nowStep - 1);
      initComponent(allComponent[nowStep - 1]);
      SDSendLeaveEvent('Return');
    }
  };

  const handleClose = () => {
    router.back();
    SDSendLeaveEvent('Close');
  };

  const initLensType = () => {
    setSelectedLensType(null);
    setSelectedBrand(null);
    setSelectedColorItem(null);
    setBrandExpand({});
  };
  /** 仅镜框流程与 lens-process 一致：返回上一步不清空折射率 */
  const initLensIndex = () => {};
  const initMap: Record<string, () => void> = {
    [LENS_TYPE_COMPONENT]: initLensType,
    [LENS_INDEX_COMPONENT]: initLensIndex,
    [COATING_COMPONENT]: initCoating,
  };

  const initComponent = (component: string) => {
    const idx = allComponent.indexOf(component);
    const afterComponents = allComponent.slice(idx + 1);
    afterComponents.forEach((com) => {
      initMap[com]?.();
    });
  };

  const scrollToTop = () => {
    if (isWap) {
      window.scrollTo({
        top: 0,
        behavior: 'instant',
      });
    } else {
      const rightPart = document.querySelector('#right-part');
      if (rightPart) {
        rightPart.scrollTo({
          top: 0,
          behavior: 'instant',
        });
      }
    }
  };

  const handleNextStep = () => {
    const nextComponent = allComponent[nowStep + 1];
    if (!nextComponent) {
      handleAddToCart();
      return;
    }
    switch (nowComponent) {
      case LENS_TYPE_COMPONENT:
        if (selectedLensType && selectedColorItem) {
          if (canModifyPrevSelection) {
            mergePrevLensSelection({
              prevLensType: encodePrevLensTypeSelection(
                getLensTypeKey(selectedLensType),
                selectedBrand?.brand_title ?? '',
                selectedColorItem?.lens_color ?? '',
              ),
            });
          }
          const lensForReport = selectedLensIndexItem ?? selectedColorItem?.lens_list?.[0];
          const shouldReportLensSkuOnNext = NON_PRESCRIPTION_TYPES.includes(
            EPrescriptionType.FRAME_ONLY,
          );
          SDLensTypePageNextClick(
            shouldReportLensSkuOnNext
              ? {
                  lens_id: lensForReport?.id != null ? `${lensForReport.id}` : '',
                  lens_code: lensForReport?.code ?? '',
                }
              : {},
          );
          setNowStep(nowStep + 1);
          scrollToTop();
        }
        break;
      case LENS_INDEX_COMPONENT:
        if (selectedLensIndexItem) {
          if (canModifyPrevSelection) {
            mergePrevLensIndexSelection(selectedLensIndexItem);
          }
          setNowStep(nowStep + 1);
          SDLensIndexPageNextClick();
          scrollToTop();
        }
        break;
      case COATING_COMPONENT:
        handleAddToCart();
        break;
      default:
        break;
    }
  };

  const getLensShow = async (
    presType: EPrescriptionType | '' | undefined,
    currentPrescription?: IPrescriptionOptions,
  ) => {
    try {
      setIsGlobalLoading(true);
      setIsLoadingLensShow(true);
      const prescriptionData = currentPrescription || prescription || getEmptyPrescription();
      let prescription_type = presType || prescriptionType;
      if (
        [EPrescriptionType.READERS, EPrescriptionType.PRESCRIPTION_READING].includes(
          prescription_type,
        )
      ) {
        prescription_type = EPrescriptionType.READING_GLASSES;
      }

      const { data, code } = await fetchLensShow({
        prescription_type,
        product_id: goodsDetail.product.id.toString(),
        od_sph: prescriptionData.od_sph,
        os_sph: prescriptionData.os_sph,
        od_cyl: prescriptionData.od_cyl,
        os_cyl: prescriptionData.os_cyl,
        os_add: prescriptionData.os_add,
        od_add: prescriptionData.od_add,
        is_prism: '2',
      });
      if (code === 200 && Array.isArray(data)) {
        const lensData = data as LensShowResponse;
        lensData.forEach((lensType) => {
          if (lensType.merge_brand === 1) {
            lensType.brand_list?.forEach((brand) => {
              if (brand.color_list?.length) {
                brand.default_lens_color = brand.color_list[0].lens_color;
              }
            });
          } else if (lensType.color_list?.length) {
            lensType.default_lens_color = lensType.color_list[0].lens_color;
          }
        });
        setLensTypeColorData(lensData);
        setLensIndexTip(lensData[0]?.index_tip || '');
        if (hasDefaultSelection) {
          const defaultColor = initDefaultLensTypeSelection(lensData);
          initDefaultLensIndexSelection(defaultColor ?? undefined);
        }
      } else {
        setLensTypeColorData([]);
        setLensIndexTip('');
      }
    } catch (error) {
      logger.error('👀', error);
    } finally {
      setIsGlobalLoading(false);
      setIsLoadingLensShow(false);
    }
  };

  const getCoatingList = async () => {
    const { data, code } = await fetchCoatingsShow();
    if (code === 200) {
      setCoatingList(data);
      if (hasDefaultSelection) {
        initDefaultCoatingSelection({
          coatingList: data,
          currency,
          userInfo,
          isFrameProcess: true,
        });
      }
    } else {
      setCoatingList([]);
    }
  };

  const handleAddToCart = async () => {
    setIsGlobalLoading(true);
    const applyCoating = shouldApplyCoatingSelection(prescriptionType, selectedColorItem, {
      isFrameProcess: true,
    });
    if (applyCoating && canModifyPrevSelection && selectedCoating) {
      mergePrevLensSelection({ prevCoatingId: `${selectedCoating.id}` });
    }
    const addToCartParams = {
      ...prescription,
      prescription_type: prescriptionType,
      lens_id: selectedLensIndexItem?.id ?? selectedColorItem?.lens_list?.[0]?.id ?? 0,
      coating_id: applyCoating && selectedCoating ? selectedCoating.id : 0,
    };

    if (
      [
        EPrescriptionType.SUNGLASSES_NON_PRESCRIPTION,
        EPrescriptionType.NON_PRESCRIPTION,
        EPrescriptionType.FRAME_ONLY,
        EPrescriptionType.READERS,
      ].includes(prescriptionType as EPrescriptionType)
    ) {
      Object.keys(addToCartParams).forEach((key) => {
        if (['0.00', 'None'].includes(addToCartParams[key])) {
          addToCartParams[key] = '';
        }
      });
    }

    const addToCartReqParams = {
      currency: currency.name,
      product_id: lensGoodsItem.product_id,
      qty: 1,
      option: addToCartParams,
    };

    SDAddToCartBtnClick();
    await addToCartFn(addToCartReqParams);
  };
  const handleSendPoints = (requestData: BaseCartRequestData) => {
    try {
      sendPoints(requestData);
    } catch (e) {
      logger.error('👀', e);
    }
  };
  /**
   * @description: 加入购物车
   * @param {*} params
   * @return {*}
   */
  const addToCartFn = async (params: BaseCartRequestData) => {
    const finalParams = handlerPrescriptionData(params);
    try {
      const res = await addToCart(finalParams);
      if (res.code === 200) {
        try {
          handleSendPoints(finalParams);
          SDAddToCartResult({
            is_success: true,
            service_code: '200',
            msg: res.msg,
          });
        } catch (e) {
          logger.error('👀', e);
        }

        sessionStorage.setItem('zl-sensors-entrance-cart-scene', '配镜流程加购');
        router.replace('/cart');
        // 跳转到购物车带特殊标记，用于app引流
        sessionStorage.setItem('toApp', '1');
      } else {
        SDAddToCartResult({
          is_success: false,
          service_code: `${res.code}`,
          msg: res.msg,
        });
      }
    } catch (err) {
      logger.error('👀', err);
      SDAddToCartResult({
        is_success: false,
        service_code: `${err.code}`,
        msg: err.msg,
      });
    } finally {
      setIsGlobalLoading(false);
    }
  };

  // 埋点相关方法
  function handleVisibilityChange() {
    if (document.visibilityState === 'hidden') {
      SDSendLeaveEvent('Else');
    }
  }

  const [minHeight, setMinHeight] = useState(0);
  useEffect(() => {
    document.body.style.backgroundColor = '#f4f4f4';
    // 解决谷歌徽章遮挡问题
    if (window.___ratingbadge_0?.style) {
      window.___ratingbadge_0.style.opacity = '0';
    }
    setMinHeight(window.innerHeight);
    return () => {
      document.body.style.backgroundColor = '';
      if (window.___ratingbadge_0?.style) {
        window.___ratingbadge_0.style.opacity = '1';
      }
    };
  }, []);

  return (
    <DefaultSelectionProvider
      hasDefaultSelection={hasDefaultSelection}
      canModifyPrevSelection={canModifyPrevSelection}
    >
      <article className={processClassName.article} style={{ minHeight: `${minHeight}px` }}>
        <ClientLeft isFrameProcess={isFrameProcess} />
        <section
          id="right-part"
          className={`right-part ${processClassName.rightPart} h-full xl:overflow-y-auto box-border`}
        >
          <div className={processClassName.rightPartContent}>
            <div
              className={`right-box ${processClassName.rightPartBox} xl:pt-[20px]`}
              id="right-box-content"
            >
              <Header
                isFrameProcess={isFrameProcess}
                onLeftBack={handleLeftBack}
                onClose={handleClose}
              />

              {nowComponent === LENS_TYPE_COMPONENT && (
                <LensType
                  isFrameProcess={isFrameProcess}
                  lensTypeColorData={lensTypeColorData}
                  selectedLensType={selectedLensType}
                  selectedBrand={selectedBrand}
                  selectedColorItem={selectedColorItem}
                  onSelectedLensType={setSelectedLensType}
                  onSelectedBrand={setSelectedBrand}
                  onSelectedColorItem={handleLensTypeSelectedColorItem}
                />
              )}
              {nowComponent === LENS_INDEX_COMPONENT && (
                <LensIndex
                  lensIndexTip={lensIndexTip}
                  selectedLensIndexItem={selectedLensIndexItem}
                  onSelectedLensIndexItem={setSelectedLensIndexItem}
                />
              )}
              {nowComponent === COATING_COMPONENT && (
                <Coating
                  coatingList={coatingList}
                  selectedCoating={selectedCoating}
                  onSelectedCoating={setSelectedCoating}
                />
              )}
            </div>
            <div className={`right-side ${processClassName.rightPartSide}`} />
          </div>
        </section>

        <Footer isFrameProcess={isFrameProcess} onNextStep={handleNextStep} />
      </article>
    </DefaultSelectionProvider>
  );
}
